/**
 * Main entry point for methodscript-communication package
 * Provides device detection and serial configuration
 */

import type { UsbDeviceId } from './device-ids.js';
import { isEmStat4Series, isFtdiDevice, isPicoDeviceByVidPid } from './device-ids.js';
import { DEVICE_CONFIGS, FTDI_STANDARD_CONFIG, PICO_CONFIG, USB_CDC_DEFAULT_CONFIG } from './serial-config.js';
import type { SerialPortConfig, DeviceConfig } from './serial-config.js';

/**
 * Device information from Web Serial API
 */
export type SerialPortInfo = {
    usbVendorId?: number;
    usbProductId?: number;
};

/**
 * Options for getting serial configuration
 */
export type GetConfigOptions = {
    /** USB device info from port.getInfo() */
    portInfo?: SerialPortInfo;
    /** Device description string (if available) */
    description?: string;
    /** Force a specific configuration */
    forceConfig?: SerialPortConfig;
};

/**
 * Get serial port configuration for a device
 *
 * @param options - Device information and options
 * @returns Serial port configuration
 *
 * @example
 * ```typescript
 * const config = getSerialConfig({
 *   portInfo: port.getInfo(),
 *   description: 'EmStat Pico'
 * });
 * await port.open(config);
 * ```
 */
export function getSerialConfig(options: GetConfigOptions = {}): SerialPortConfig {
    const { portInfo, description, forceConfig } = options;

    // If forced config provided, use it
    if (forceConfig) {
        return forceConfig;
    }

    // Try to match by description first (most reliable for Pico detection)
    if (description) {
        const descLower = description.toLowerCase();
        const matchedConfig = DEVICE_CONFIGS.find((config) => config.descriptionPatterns.some((pattern) => descLower.includes(pattern)));

        if (matchedConfig) {
            return matchedConfig.serialConfig;
        }
    }

    // If no description match, use VID/PID
    if (portInfo?.usbVendorId !== undefined && portInfo?.usbProductId !== undefined) {
        const deviceId: UsbDeviceId = {
            vendorId: portInfo.usbVendorId,
            productId: portInfo.usbProductId,
        };

        // Check if it's a Pico device by VID/PID first
        if (isPicoDeviceByVidPid(deviceId)) {
            return PICO_CONFIG;
        }

        // EmStat4 series (USB-CDC): use 921600
        if (isEmStat4Series(deviceId)) {
            return FTDI_STANDARD_CONFIG;
        }

        // Other FTDI devices: use 921600
        if (isFtdiDevice(deviceId)) {
            return FTDI_STANDARD_CONFIG;
        }
    }

    // Default to USB-CDC config
    return USB_CDC_DEFAULT_CONFIG;
}

/**
 * Check if a device is a Pico device based on description
 */
export function isPicoDevice(description?: string): boolean {
    if (!description) {
        return false;
    }

    const descLower = description.toLowerCase();
    const picoConfig = DEVICE_CONFIGS.find((config) => config.isPico);

    if (!picoConfig) {
        return false;
    }

    return picoConfig.descriptionPatterns.some((pattern) => descLower.includes(pattern));
}

// Re-export types and constants
export type { UsbDeviceId, SerialPortConfig, DeviceConfig };
export { FTDI_STANDARD_CONFIG, PICO_CONFIG, USB_CDC_DEFAULT_CONFIG, DEVICE_CONFIGS };
export * from './device-ids.js';
